import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';

import { API_EXCEPTION_MSG, DataLayer, SCROLL_TARGET_TYPE, getDataNotFoundMessageObject } from '../../../Common';
import CircularProgress from '../../atoms/CircularProgress';
import Grid from '../../atoms/Grid';
import CustomerInfoPreview from '../../organisms/CustomerInfoPreview';
import NegotiationDetailPreview from '../../organisms/NegotiationDetailPreview';
import PreviewSideNavigation from '../../organisms/PreviewSideNavigation';
import { AppState } from '../../../redux/modules/store';

import ApiError, { ApiErrorProps } from '../../molecules/ApiError';

const SCROLL_CONTAINER_ID = 'scroll-container';

/**
 * スクロールターゲット
 */
export type scrollTargetType = typeof SCROLL_TARGET_TYPE[keyof typeof SCROLL_TARGET_TYPE];

export interface ScrollTargetInfo {
  scrollTargetType: scrollTargetType;
  id: string;
}
export interface SummaryPreviewContainerProps {
  defaultViewportMetaContent: string;
  userScalableViewportMetaContent: string;
  /**
   * 初期状態か否か
   */
  isBeforeInitialize: boolean;
  /**
   * 顧客ID
   */
  customerId: string;
  /**
   * 編集可否
   */
  isEditable: boolean;
  /**
   * データが見つからなかった場合のエラーメッセージタイプ
   */
  errorMessageType: DataLayer;
  /**
   * モーダル上に表示するかどうか(デフォルト false)
   */
  onModal?: boolean;
  /**
   * 初期スクロール情報
   */
  scrollTargetInfo?: ScrollTargetInfo;
  getConfidentialPermission?: () => Promise<string | null>;
  postConfidentialPermission?: (permissionType: string) => void;
  openPermissionModal?: (actType: string, negotiationId?: string, productId?: string, actionId?: string) => Promise<void>;
  previewSummaryClassName?: string;
}

const SummaryPreviewContainer: React.FC<SummaryPreviewContainerProps> = ({
  defaultViewportMetaContent,
  userScalableViewportMetaContent,
  isBeforeInitialize,
  isEditable,
  errorMessageType,
  onModal = false,
  scrollTargetInfo,
  openPermissionModal,
  previewSummaryClassName = '',
}) => {
  // global State
  const fetchLoadingStatus = useSelector((state: AppState) => state.loadingStatus.fetchCustomerInfoAll);
  const { customerId: stateCustomerId, customerNumber: stateCustomerNumber } = useSelector((state: AppState) => state.customerInfoAll);

  // お客さま情報が存在しないフラグ
  const isDataNotFound = stateCustomerId === 0 && stateCustomerNumber === '';
  /**
   * 指定した項目へスクロールするナビゲーションの表示有無
   * モーダル上に表示の場合はfalse
   */
  const hasLinkNavigation = !onModal;
  /**
   * データが存在しない場合に表示するメッセージ
   */
  const dataNotFoundMessageObject = getDataNotFoundMessageObject(errorMessageType);

  const errorDetailProps: ApiErrorProps = {
    apiName: 'customerInfoAllApi',
    jobDatetime: fetchLoadingStatus.result?.jobDateTime ? `${fetchLoadingStatus.result.jobDateTime}` : '',
    errorStatus: fetchLoadingStatus.result?.resultCd || '',
    errorTitle: fetchLoadingStatus.result?.resultCd === '404' ? dataNotFoundMessageObject.errorTitle : API_EXCEPTION_MSG,
    errorMessageList: dataNotFoundMessageObject.errorMessageList,
  };

  return (
    <>
      <div className='layout-tab__content'>
        {fetchLoadingStatus.isLoading ? (
          <div className={onModal ? 'layout-center-child' : 'layout-center'} data-testid='loadingArea'>
            <div className='u-text-center'>
              <CircularProgress isIndeterminate hasBackground={false} />
              <p>ページを読み込んでいます...</p>
            </div>
          </div>
        ) : !isBeforeInitialize && isDataNotFound ? (
          // データが見つからない場合
          <ApiError {...errorDetailProps} onModal={true} />
        ) : (
          <Grid>
            <Grid.Row>
              {/* 追従サイドメニュー */}
              {hasLinkNavigation && (
                <Grid.Col colS={0} colM={3} col={3} className='u-pt16'>
                  {/* 商品登録と共通化 */}
                  <div className={classNames('u-mb16 u-ml16 u-overflow-auto preview-scroll', previewSummaryClassName)}>
                    <PreviewSideNavigation scrollContainerId={SCROLL_CONTAINER_ID} />
                  </div>
                </Grid.Col>
              )}

              {/* プレビュー画面 */}
              <Grid.Col colS={12} colM={hasLinkNavigation ? 9 : 12} col={hasLinkNavigation ? 9 : 12} className='u-pt16 u-pl16'>
                <div
                  className={classNames('u-flex u-flex-column u-pr16 u-overflow-auto preview-scroll', previewSummaryClassName)}
                  id={SCROLL_CONTAINER_ID}>
                  {/* お客様情報セクション */}
                  <div id='customerInfo' className='container u-bg-white u-pt0 preview-header'>
                    <CustomerInfoPreview isEditable={isEditable} openPermissionModal={openPermissionModal} />
                  </div>
                  {/* 商談情報セクション */}
                  <NegotiationDetailPreview
                    defaultViewportMetaContent={defaultViewportMetaContent}
                    userScalableViewportMetaContent={userScalableViewportMetaContent}
                    scrollContainerId={SCROLL_CONTAINER_ID}
                    scrollTargetInfo={scrollTargetInfo}
                    isEditable={isEditable}
                    onModal={onModal}
                    openPermissionModal={openPermissionModal}
                  />
                </div>
              </Grid.Col>
            </Grid.Row>
          </Grid>
        )}
      </div>
    </>
  );
};

export default SummaryPreviewContainer;
